import { NavLink } from 'react-router';
import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  BookOpen,
  ShoppingBag,
  ClipboardList,
  BarChart3,
  QrCode,
  Settings,
  Users,
  CreditCard,
  TicketPercent,
  MessagesSquare,
} from 'lucide-react';
import { useAuth } from '@/shared/hooks/useAuth';
import { useConversationsNavBadge, type NavItemGate } from '@/features/conversations/hooks/useConversationsNavBadge';
import logoRight from '@/assets/vapt-menu-logo-right.png';
import styles from './Sidebar.module.css';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  permission: string | null;
  /**
   * Destaca o item só na rota exata (o padrão): "Pedidos" não acende em
   * "/orders/history". Falso acende também nas rotas filhas.
   */
  end?: boolean;
  /**
   * Gate opcional, além da permissão: um hook que diz se o item aparece e o
   * contador ao lado do rótulo. Só roda para quem tem a permissão.
   */
  useGate?: () => NavItemGate;
}

const navItems: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, permission: null },
  { to: '/catalog',   label: 'Cardápio',  icon: BookOpen,         permission: 'catalog.read' },
  { to: '/promotions', label: 'Promoções', icon: TicketPercent,   permission: 'catalog.read' },
  { to: '/orders',    label: 'Pedidos',   icon: ShoppingBag,      permission: 'orders.read' },
  // O Atendimento do Atendente IA (change customer-agent-handoff): só com a
  // área disponível para a loja, e com as conversas alertadas no contador.
  {
    to: '/conversations',
    label: 'Atendimento',
    icon: MessagesSquare,
    permission: 'conversations.read',
    // Acende também na conversa aberta (/conversations/:conversationId).
    end: false,
    useGate: useConversationsNavBadge,
  },
  { to: '/orders/history', label: 'Histórico', icon: ClipboardList, permission: 'orders.read' },
  { to: '/reports',   label: 'Relatórios', icon: BarChart3,       permission: 'reports.read' },
  { to: '/qr-code',   label: 'QR Code',   icon: QrCode,           permission: null },
  { to: '/team',      label: 'Equipe',    icon: Users,            permission: 'team.manage' },
  { to: '/billing',   label: 'Assinatura', icon: CreditCard,     permission: 'billing.read' },
  { to: '/settings',  label: 'Configurações', icon: Settings,     permission: 'settings.read' },
];

/** "9+" acima de nove. */
function navCountText(count: number): string {
  return count > 9 ? '9+' : String(count);
}

export interface SidebarProps {
  onNavigate?: () => void;
  variant?: 'desktop' | 'mobileDrawer';
}

export function Sidebar({ onNavigate, variant = 'desktop' }: SidebarProps) {
  const { selectedTenant, hasPermission } = useAuth();

  const visibleItems = navItems.filter(
    ({ permission }) => permission === null || hasPermission(permission),
  );

  return (
    <aside
      className={`${styles.sidebar} ${variant === 'mobileDrawer' ? styles.mobileDrawer : styles.desktop}`}
      data-sidebar-variant={variant}
    >
      <div className={styles.logo}>
        <img src={logoRight} alt="Vapt Menu" className={styles.logoImg} />
      </div>

      {selectedTenant && (
        <div className={styles.tenantName}>{selectedTenant.name}</div>
      )}

      <nav className={styles.nav} aria-label="Navegação principal">
        {visibleItems.map((item) =>
          item.useGate ? (
            <GatedNavItem key={item.to} item={item} useGate={item.useGate} onNavigate={onNavigate} />
          ) : (
            <NavItemLink key={item.to} item={item} onNavigate={onNavigate} />
          ),
        )}
      </nav>
    </aside>
  );
}

interface NavItemLinkProps {
  item: NavItem;
  onNavigate?: () => void;
  count?: number | null;
  countLabel?: string;
}

function GatedNavItem({ item, useGate, onNavigate }: NavItemLinkProps & { useGate: () => NavItemGate }) {
  const gate = useGate();
  if (!gate.visible) return null;
  return <NavItemLink item={item} onNavigate={onNavigate} count={gate.count} countLabel={gate.countLabel} />;
}

function NavItemLink({ item, onNavigate, count, countLabel }: NavItemLinkProps) {
  const { to, label, icon: Icon } = item;
  const showCount = typeof count === 'number' && count > 0;
  return (
    <NavLink
      to={to}
      end={item.end ?? true}
      onClick={onNavigate}
      // O contador é anunciado junto do rótulo ("Atendimento, 2 clientes esperando").
      aria-label={showCount ? `${label}, ${countLabel ?? navCountText(count)}` : undefined}
      className={({ isActive }) =>
        [styles.navLink, isActive && styles.navLinkActive]
          .filter(Boolean)
          .join(' ')
      }
    >
      <Icon size={20} strokeWidth={1.5} className={styles.navIcon} />
      {label}
      {showCount && (
        <span className={styles.navCount} aria-hidden="true" data-testid={`nav-count-${to}`}>
          {navCountText(count)}
        </span>
      )}
    </NavLink>
  );
}
